import { useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { toPng } from "html-to-image";
import TopStatusBar from "./dashboard/TopStatusBar";
import HeroMetric from "./dashboard/HeroMetric";
import TradingControlStrip from "./dashboard/TradingControlStrip";
import ActivePositionsPanel from "./dashboard/ActivePositionsPanel";
import PositionPriceChart from "./dashboard/PositionPriceChart";
import TradeDistribution from "./dashboard/TradeDistribution";
import PnlTimeline from "./dashboard/PnlTimeline";
import DecisionLog from "./dashboard/DecisionLog";
import AnalysisReport from "./dashboard/AnalysisReport";
import ArticlesPanel from "./dashboard/ArticlesPanel";
import XPostsPanel from "./dashboard/XPostsPanel";
import NetworkGraph from "./dashboard/NetworkGraph";
import LiveSpotFeed from "./dashboard/LiveSpotFeed";
import { useTheme } from "../context/ThemeContext";
import { usePmaDashboardData } from "../hooks/usePmaDashboardData";
import { getAnalysisLevels } from "../lib/mapSupabaseToDashboard";
import { isSupabaseConfigured } from "../lib/supabase";

export default function Dashboard() {
  const { theme } = useTheme();
  const [selectedTicker, setSelectedTicker] = useState(null);
  const captureRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const {
    raw,
    view,
    loading,
    error,
    lastFetchedAt,
    hasData,
    refresh,
    getPriceChart,
    stats,
    log,
    report,
    pnl,
    distribution,
    articles,
    xPosts,
    positions,
    tradingConfig,
    tickers,
    tableDiagnostics,
  } = usePmaDashboardData();

  const showEmptyBanner = !loading && !error && !hasData && isSupabaseConfigured;
  const isDev = process.env.NODE_ENV === "development";

  const priceChart = useMemo(
    () => getPriceChart(selectedTicker),
    [getPriceChart, selectedTicker],
  );

  const filteredPositions = useMemo(() => {
    if (!selectedTicker) return positions;
    return positions.filter((p) => p.ticker === selectedTicker);
  }, [positions, selectedTicker]);

  const selectedLevels = useMemo(
    () => getAnalysisLevels(raw?.analysis, raw?.positions, selectedTicker),
    [raw, selectedTicker],
  );

  const handleRefresh = async () => {
    const result = await refresh();
    const loaded =
      result.raw &&
      (result.raw.dailyState ||
        result.raw.tradingConfig ||
        (result.raw.decisions?.length ?? 0) > 0 ||
        (result.raw.positions?.length ?? 0) > 0);
    if (result.ok) {
      toast.success("Data refreshed", {
        description: loaded ? "Loaded from Supabase." : "Connected; tables may be empty.",
      });
    } else {
      toast.error("Refresh failed", {
        description: result.error?.message || "Check Supabase config and RLS.",
      });
    }
  };

  const buildSnapshot = () => ({
    meta: {
      product: "PushNow Dashboard",
      generatedAt: new Date().toISOString(),
      lastFetchedAt: lastFetchedAt?.toISOString(),
    },
    raw,
    derived: view,
    filter: { selectedTicker },
  });

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const exportFilename = (ext) => {
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    return `pushnow-dashboard-${ts}.${ext}`;
  };

  const handleExportJson = () => {
    const blob = new Blob([JSON.stringify(buildSnapshot(), null, 2)], {
      type: "application/json",
    });
    downloadBlob(blob, exportFilename("json"));
    toast.success("JSON exported");
  };

  const handleExportPng = async () => {
    if (!captureRef.current || exporting) return;
    setExporting(true);
    try {
      const bgColor =
        getComputedStyle(document.body).getPropertyValue("background-color") ||
        (theme === "dark" ? "#0A0A0A" : "#F4F4F0");
      const dataUrl = await toPng(captureRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: bgColor.trim() || (theme === "dark" ? "#0A0A0A" : "#F4F4F0"),
        filter: (node) => {
          if (node?.getAttribute && node.getAttribute("data-sonner-toaster") !== null) {
            return false;
          }
          return true;
        },
      });
      const res = await fetch(dataUrl);
      downloadBlob(await res.blob(), exportFilename("png"));
      toast.success("PNG exported");
    } catch (e) {
      toast.error("PNG export failed", { description: e?.message });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Toaster
        theme={theme === "dark" ? "dark" : "light"}
        position="bottom-right"
        toastOptions={{
          style: {
            background: "hsl(var(--surface))",
            color: "hsl(var(--foreground))",
            border: "1px solid hsl(var(--border))",
            borderRadius: 2,
            fontFamily: "JetBrains Mono",
            fontSize: 12,
          },
        }}
      />
      <TopStatusBar
        loading={loading}
        lastFetchedAt={lastFetchedAt}
        marketRegime={tradingConfig?.market_regime}
        dailyStatus={stats?.status}
        hasData={hasData}
        error={error}
        onRefresh={handleRefresh}
        onExportJson={handleExportJson}
        onExportPng={handleExportPng}
      />

      {error && !loading && (
        <div
          className="mx-3 sm:mx-5 mt-2 px-3 py-2 border border-magenta text-magenta text-xs font-mono"
          style={{ borderRadius: 2 }}
          data-testid="supabase-error-banner"
        >
          {error.message || String(error)} — Run supabase/seed.sql and supabase/rls_anon_read.sql in your project.
        </div>
      )}

      {showEmptyBanner && (
        <div
          className="mx-3 sm:mx-5 mt-2 px-3 py-2 border border-border bg-surface-muted text-text-secondary text-xs font-mono leading-relaxed"
          style={{ borderRadius: 2 }}
          data-testid="supabase-empty-banner"
        >
          Connected to Supabase, but all tables returned no rows. This usually means seed data was not
          applied or RLS blocks <code className="text-foreground">anon</code> SELECT. In SQL Editor run{" "}
          <strong className="text-foreground">supabase/seed.sql</strong> then{" "}
          <strong className="text-foreground">supabase/rls_anon_read.sql</strong>, verify in Table Editor,
          then restart <code className="text-foreground">yarn start</code>. See supabase/README.md — or run{" "}
          <code className="text-foreground">yarn supabase:verify</code>.
        </div>
      )}

      {isDev && tableDiagnostics.length > 0 && !loading && (
        <div
          className="mx-3 sm:mx-5 mt-2 px-3 py-2 border border-dashed border-border text-[10px] font-mono text-text-secondary"
          style={{ borderRadius: 2 }}
          data-testid="supabase-diagnostics"
        >
          <span className="label-micro block mb-1 text-foreground">Dev diagnostics (row counts)</span>
          {tableDiagnostics.map((d) => (
            <span key={d.table} className="inline-block mr-3">
              {d.table}: {d.error ? `ERR ${d.error}` : d.count}
            </span>
          ))}
        </div>
      )}

      <main
        ref={captureRef}
        className="flex-1 w-full max-w-[1920px] mx-auto px-3 sm:px-5 py-4 flex flex-col gap-3 sm:gap-4 bg-background"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <HeroMetric stats={stats} tradingConfig={tradingConfig} dailyState={view.dailyState} />
          <TradingControlStrip
            tickers={tickers}
            selectedTicker={selectedTicker}
            onSelectTicker={setSelectedTicker}
            marketRegime={tradingConfig?.market_regime}
            dailyStatus={stats?.status}
            loading={loading}
            onRefresh={handleRefresh}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <ActivePositionsPanel positions={filteredPositions} />
          <AnalysisReport report={report} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <NetworkGraph
            graph={view.analysisGraph}
            selectedTicker={selectedTicker}
            onSelectSymbol={setSelectedTicker}
          />
          <LiveSpotFeed levels={selectedLevels} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <PositionPriceChart chartData={priceChart} />
          <TradeDistribution data={distribution} />
          <PnlTimeline data={pnl} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <ArticlesPanel articles={articles} />
          <XPostsPanel posts={xPosts} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <DecisionLog rows={log} />
          <FooterDisclaimer hasData={hasData} tableDiagnostics={isDev ? tableDiagnostics : []} />
        </div>
      </main>
    </div>
  );
}

const FooterDisclaimer = ({ hasData, tableDiagnostics = [] }) => (
  <div
    className="panel lg:col-span-5 p-4 flex flex-col gap-2"
    data-testid="footer-disclaimer"
  >
    <span className="label-uppercase text-magenta">Disclaimer</span>
    <p className="text-xs leading-relaxed text-text-secondary font-mono">
      PushNow Dashboard reads portfolio state, positions, decisions, analysis, articles, and
      social posts from Supabase. Price charts reflect position levels, not live market feeds.
      Not investment advice.
    </p>
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      <span className="label-micro px-1.5 py-0.5 border border-border" style={{ borderRadius: 2 }}>
        PUSHNOW · SUPABASE
      </span>
      <span
        className={`label-micro px-1.5 py-0.5 border ${
          hasData ? "border-positive text-positive" : "border-magenta text-magenta"
        }`}
        style={{ borderRadius: 2 }}
        data-testid="data-source-badge"
      >
        {hasData ? "DATA LOADED" : "EMPTY / RLS?"}
      </span>
    </div>
    {!hasData && tableDiagnostics.length > 0 && (
      <p className="text-[10px] font-mono text-text-secondary mt-2">
        Counts: {tableDiagnostics.map((d) => `${d.table}=${d.count}`).join(", ")}
      </p>
    )}
  </div>
);
